import { useState } from 'react'
import { shareOutward } from '../lib/shareOutward'
import { createPortal } from 'react-dom'
import { Share2, Link as LinkIcon, Check } from 'lucide-react'
import { SendToFriendList } from './SendToFriend'
import { CloseButton } from './UI'
import { showShareText } from '../lib/shareText'
import { shareReview } from '../lib/shareReview'
import { useDialog } from '../hooks/useDialog'

// ── Unified Share sheet: in-app Recommend (primary) + external Share (secondary) ─
// Slides up from the bottom. Portal'd to document.body to avoid the iOS Safari
// transformed-ancestor fixed-positioning bug.
//
// Shares a SHOW by default. Pass `review` to make it review-aware instead: the
// internal half still sends the show to a friend but seeds the note with your
// review text ("send your review"), and the external half hands off to the branded
// review card (shareReview) rather than a bare show link.

export function ShareSheet({ show, user, profile, rating, review, link, onClose }) {
  const dialogRef = useDialog(onClose)
  // Three modes: SHOW (default), REVIEW (review-aware), or a generic LINK (a list,
  // profile, anything with { label, title, url, poster? }) — link mode is external-only
  // since there's no in-app "recommend a list/profile to a friend" path.
  const isLink = !!link
  const isReview = !!review && !isLink
  // Normalized show the sheet operates on — the passed show, or the review's show.
  const sh = show || (review ? { id: review.show_id, name: review.show_name, poster_path: review.show_poster } : null)
  const revRating = isReview ? (review.rating ?? rating ?? 0) : (rating || 0)
  const [msg,        setMsg]        = useState(isReview ? (review.body || '').slice(0, 400) : '')
  const [shareUrl,   setShareUrl]   = useState(null)
  const [copied,     setCopied]     = useState(false)
  const [externalBusy, setExternalBusy] = useState(false)
  if (!sh && !isLink) return null

  // Build the share URL once. Review mode links to the show; show mode uses the
  // rich /share/v2 link that carries the rating.
  const buildUrl = () => {
    if (shareUrl) return shareUrl
    let url
    if (isLink) {
      url = link.url
    } else if (isReview) {
      url = `${window.location.origin}/show/${sh.id}`
    } else {
      const slug       = (user?.email?.split('@')[0] || 'anon').replace(/[^a-z0-9]/gi, '').slice(0, 12).toLowerCase()
      const ratingCode = Math.round(revRating * 10)
      url = `${window.location.origin}/share/v2/${sh.id}-${ratingCode}-${slug}`
    }
    setShareUrl(url)
    return url
  }

  const shareText = isLink
    ? (link.title || 'NextEp')
    : showShareText({ showName: sh.name, rating: revRating, sharerName: profile?.display_name || profile?.username })

  const handleNativeShare = async () => {
    setExternalBusy(true)
    const url = buildUrl()
    // shareOutward drops the sentence for URLs that unfurl into a real card (/show/,
    // /share/ both do) so iMessage renders ONE rich bubble instead of a sentence plus a
    // preview — see src/lib/shareOutward.js.
    const res = await shareOutward({ url, text: shareText, title: `${isLink ? link.title : sh.name}, NextEp`, source: isLink ? 'list' : isReview ? 'review' : 'show' })
    if (res === 'copied') { setCopied(true); setTimeout(() => setCopied(false), 2500) }
    setExternalBusy(false)
  }

  // Review mode external share → the branded review card (image + link).
  const handleReviewShare = async () => {
    setExternalBusy(true)
    const author = review.author?.display_name || review.author?.username || profile?.display_name || profile?.username
    try {
      const r = await shareReview({ showId: review.show_id, showName: review.show_name, author, rating: revRating, body: review.body })
      if (r === 'copied') { setCopied(true); setTimeout(() => setCopied(false), 2500) }
    } catch (e) { console.error('review share error:', e) }
    setExternalBusy(false)
  }

  const handleCopyLink = async () => {
    const url = buildUrl()
    try { await navigator.clipboard?.writeText(url) } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 2500)
  }

  return createPortal((
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 'var(--z-share)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        fontFamily: 'var(--font)',
      }}
    >
      <div
        ref={dialogRef} role="dialog" aria-modal="true" aria-label="Share this show" onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--sheet)',
          border: '1px solid var(--border)',
          borderRadius: '22px 22px 0 0',
          width: '100%',
          maxHeight: '88dvh',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
          '--nxa': 'sheetUp 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(var(--ink-rgb),0.2)' }}/>
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '8px 18px 16px', borderBottom: '1px solid var(--border)',
        }}>
          {(isLink ? link.poster : sh.poster_path) && (
            <img
              src={isLink ? link.poster : `https://image.tmdb.org/t/p/w92${sh.poster_path}`}
              alt=""
              style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: 'var(--text-muted)', fontSize: 10, fontWeight: 600,
              letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 3,
            }}>{isLink ? (link.label || 'Share') : isReview ? 'Share this review' : 'Share this show'}</div>
            <div style={{
              color: 'var(--text)', fontWeight: 700, fontSize: 16, lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>{isLink ? link.title : sh.name}</div>
          </div>
          <CloseButton onClick={onClose} size={30} />
        </div>

        {/* ── PRIMARY: In-app Recommend (shows/reviews only) ─────────────── */}
        {!isLink && (
        <div style={{ padding: '16px 18px 8px' }}>
          <div style={{
            color: 'var(--amber)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span>✨</span><span>{isReview ? 'Send your review to a friend' : 'Recommend to a friend'}</span>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12, lineHeight: 1.4 }}>
            {isReview
              ? "Send it to someone on NextEp, your review rides along as the note."
              : "Send it directly to someone on NextEp. They'll get a notification with your note."}
          </div>

          <SendToFriendList
            show={sh}
            user={user}
            msg={msg}
            onMsgChange={setMsg}
            onSent={() => { /* Stay open; user may want to send to others or external */ }}
          />
        </div>
        )}

        {/* ── SECONDARY: External share ─────────────────────────────────── */}
        <div style={{ padding: '18px 18px 4px', borderTop: isLink ? 'none' : '1px solid var(--border)', marginTop: isLink ? 4 : 14 }}>
          <div style={{
            color: 'var(--text-dim)', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 12,
          }}>{isLink ? 'Share' : 'Share elsewhere'}</div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={isReview ? handleReviewShare : handleNativeShare}
              disabled={externalBusy}
              style={{
                flex: 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                padding: '14px 8px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius:'var(--radius)',
                color: 'var(--text)', cursor: 'pointer',
                fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500,
              }}
            >
              <Share2 size={18} style={{ color: 'var(--amber)' }}/>
              <span>{externalBusy ? '…' : (isReview ? 'Share card' : 'Share link')}</span>
            </button>

            <button
              onClick={handleCopyLink}
              style={{
                flex: 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                padding: '14px 8px',
                background: copied ? 'color-mix(in srgb, var(--state-good) 12%, transparent)' : 'var(--bg-card)',
                border: `1px solid ${copied ? 'color-mix(in srgb, var(--state-good) 35%, transparent)' : 'var(--border)'}`,
                borderRadius:'var(--radius)',
                color: copied ? 'var(--green)' : 'var(--text)', cursor: 'pointer',
                fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500,
              }}
            >
              {copied ? <Check size={18}/> : <LinkIcon size={18} style={{ color: 'var(--amber)' }}/>}
              <span>{copied ? 'Copied!' : 'Copy link'}</span>
            </button>
          </div>
        </div>
      </div>

      
    </div>
  ), document.body)
}
